import { State, Action, StateContext, Selector, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { EngineStateModel } from './engine.state.model';
import {
  Load,
  LoadSuccess,
  SetReady,
  SetGraph,
  SetEngineChapter,
  SetEnginePosition,
  SetEngineChapterInit,
  SynchronizeVisibleNodes,
  ClearGraph,
  ResetEnginePosition,
  ToggleCameraBar,
  ToggleLabels,
  SetActiveTool,
  ToggleAnimation,
  CenterCameraOnVisible,
  SetDisplay,
  SetVisible,
  SynchronizeRenderableNodes,
  SynchronizePickableNodes,
  SetScrollMode,
  SetXrayMode,
  SetAutoplay,
  SetObjectLayerOrder,
  AutoSortObjectLayers,
  ResetObjectLayers,
  SetObjectDisplayName,
  SetPostProcessingEffects,
  SetProperties,
  SetTransparencyMode,
  ExpandViewTreeNode,
  CollapseViewTreeNode,
  HoverNode,
  SetLabelEditNode,
  QueryNodeInfo,
  QueryNodeInfoSuccess,
  SetNodeInfo,
  QueryNodeInfoFailure,
  PlayAudio,
  ToggleProgressBar,
  PlayChapterAnimation,
  SetAnimationPlaying,
  SetContextMenu,
  SetPlaybackSpeed
} from './engine.actions';
import { BdGraphService } from '../../../lib/graph/graph.service';
import { ViewTreeNode } from '../models/view.tree-node';
import { EngineService } from '../engine.service';
import { BdGraph, BdGraphStateMap, BdGraphFactory } from 'src/lib/graph/graph.model';
import { EnginePositionModel } from '../models/engine-position.model';
import { EngineVisibilityEnum } from '../models/engine-visibility.enum';
import { EngineCameraState } from './camera/engine.camera.state';
import { ToolEnum } from '../models/tool.enum';
import { ScrollModeEnum } from '../models/scroll-mode.enum';
import { finalize } from 'rxjs/operators';
import { WikiService } from 'src/app/shared/wiki.service';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { get } from 'lodash';
import { AnimationService } from '../animation.service';
import { Injectable } from '@angular/core';

declare const Human: any;

const enginePositionDefaults = new EnginePositionModel(0, 0, 0, 0, EngineVisibilityEnum.Hidden);


@State<EngineStateModel>({
  name: 'engine',
  defaults: {
    loading: false,
    ready: false,
    viewGraph: BdGraphFactory<ViewTreeNode>(),
    hoveredNode: undefined,
    labelEditNode: undefined,
    queryingWiki: false,
    engine: '',
    chapter: '',
    chapterInit: {
      id: '',
      init: false,
      camera: false,
      annotations: false,
      graph: false,
      properties: false,
      particles: false,
      clips: false,
      postprocessing: false,
      transparency: false
    },
    showProgressBar: true,
    showCameraBar: true,
    showLabels: true,
    activeTool: ToolEnum.Select,
    animationPlaying: true,
    autoplay: true,
    playAudio: false,
    display: true,
    visible: true,
    scrollMode: ScrollModeEnum.Capture,
    playbackSpeed: 1,
    xrayModeEnabled: false,
    contextmenu: {
      node: null,
      hit: null
    }
  },
  children: [
    EngineCameraState
  ]
})
@Injectable()
export class EngineState {
  /**
   * Selectors
   */
  @Selector()
  static getEngine(state: EngineStateModel) {
    return state.engine;
  }

  @Selector()
  static getReady(state: EngineStateModel) {
    return state.ready;
  }

  @Selector()
  static getDisplay(state: EngineStateModel) {
    return state.display;
  }

  @Selector()
  static getScrollMode(state: EngineStateModel) {
    return state.scrollMode;
  }

  @Selector()
  static getVisible(state: EngineStateModel) {
    return state.visible;
  }

  @Selector()
  static getViewGraph(state: EngineStateModel) {
    return state.viewGraph;
  }

  @Selector()
  static getViewGraphNode(state: EngineStateModel) {
    return (objectId: string) => {
      return state.viewGraph.nodes[objectId];
    };
  }


  @Selector()
  static getEnginePosition(state: EngineStateModel) {
    return state.enginePosition;
  }

  @Selector()
  static getChapter(state: EngineStateModel) {
    return state.chapter;
  }

  @Selector()
  static getActiveTool(state: EngineStateModel) {
    return state.activeTool;
  }

  @Selector()
  static getShowCameraBar(state: EngineStateModel) {
    return state.showCameraBar;
  }

  @Selector()
  static getShowProgressBar(state: EngineStateModel) {
    return state.showProgressBar;
  }

  @Selector()
  static getShowLabels(state: EngineStateModel) {
    return state.showLabels;
  }

  @Selector()
  static getChapterInit(state: EngineStateModel) {
    return state.chapterInit;
  }

  @Selector()
  static getAnimationPlaying(state: EngineStateModel) {
    return state.animationPlaying;
  }

  @Selector()
  static getPlayAudio(state: EngineStateModel) {
    return state.playAudio;
  }

  @Selector()
  static getAutoplay(state: EngineStateModel) {
    return state.autoplay;
  }

  @Selector()
  static getXrayModeEnabled(state: EngineStateModel) {
    return state.xrayModeEnabled;
  }

  @Selector()
  static getHoveredNode(state: EngineStateModel) {
    return state.hoveredNode;
  }

  @Selector()
  static getLabelEditNode(state: EngineStateModel) {
    return state.labelEditNode;
  }

  @Selector()
  static getQuerying(state: EngineStateModel) {
    return state.queryingWiki;
  }

  @Selector()
  static getContextmenu(state: EngineStateModel) {
    return state.contextmenu;
  }

  @Selector()
  static getPlaybackSpeed(state: EngineStateModel) {
    return state.playbackSpeed;
  }

  /**
   * Actions
   */
  @Action(Load)
  load(context: StateContext<EngineStateModel>, action: Load) {
    context.dispatch(new LoadSuccess('engine'));
  }

  @Action(LoadSuccess)
  loadSuccess({ patchState }: StateContext<EngineStateModel>, action: LoadSuccess) {
    patchState({
      engine: action.result
    });
  }

  @Action(SetReady)
  setReady(context: StateContext<EngineStateModel>, action: SetReady) {
    context.patchState({
      ready: action.ready
    });
  }

  @Action(SetDisplay)
  setDisplay(context: StateContext<EngineStateModel>, action: SetDisplay) {
    context.patchState({
      display: action.display
    });
  }

  @Action(SetScrollMode)
  setZoomViaScrollAllowed(context: StateContext<EngineStateModel>, action: SetScrollMode) {
    context.patchState({
      scrollMode: action.scrollMode
    });
  }

  @Action(SetVisible)
  setVisible(context: StateContext<EngineStateModel>, action: SetVisible) {
    context.patchState({
      visible: action.visible
    });
  }

  @Action(SetEnginePosition)
  setEnginePosition(context: StateContext<EngineStateModel>, action: SetEnginePosition) {
    context.patchState({
      enginePosition: {
        ...action.position,
        right: action.position.left + action.position.width,
        bottom: action.position.top + action.position.height
      }
    });
  }

  @Action(ResetEnginePosition)
  resetEnginePosition(context: StateContext<EngineStateModel>, action: ResetEnginePosition) {
    context.patchState({
      enginePosition: enginePositionDefaults
    });
  }

  @Action(SetEngineChapter)
  setChapter(context: StateContext<EngineStateModel>, action: SetEngineChapter) {
    context.patchState({
      chapter: action.chapterId
    });
  }

  @Action(SetEngineChapterInit)
  setChapterInit(context: StateContext<EngineStateModel>, action: SetEngineChapterInit) {
    context.patchState({
      chapterInit: action.chapterInit
    });
  }

  @Action(SetActiveTool)
  setActiveTool(context: StateContext<EngineStateModel>, action: SetActiveTool) {
    context.patchState({
      activeTool: action.tool
    });
  }

  /**
   * Graph Actions #TODO: Move into own state
   */
  @Action(SetGraph)
  setGraph(context: StateContext<EngineStateModel>, action: SetGraph) {
    // Set graph state
    context.patchState({
      viewGraph: {...action.graph}
    });
  }

  @Action(ClearGraph)
  clearGraph(context: StateContext<EngineStateModel>, action: SetGraph) {
    context.patchState({
      viewGraph: BdGraphFactory<ViewTreeNode>()
    });
  }

  @Action(SetXrayMode)
  setXrayMode(context: StateContext<EngineStateModel>, action: SetXrayMode) {
    context.patchState({
      xrayModeEnabled: action.enabled
    });
  }

  @Action(CenterCameraOnVisible)
  centerCameraOnVisible(context: StateContext<EngineStateModel>, action: CenterCameraOnVisible) {
    const visibleLeaves = this.engineService.getVisibleLeaves();
    const boundary = this.engineService.getBoundaryFromObjects(visibleLeaves);
    const lookAt = this.engineService.getCameraLookAt();
    lookAt.look = {
      x: (boundary.xmin + boundary.xmax) / 2.0,
      y: (boundary.ymin + boundary.ymax) / 2.0,
      z: (boundary.zmin + boundary.zmax) / 2.0
    };
    Human.renderer.camera.fly.flyTo(lookAt);
  }

  @Action(ToggleProgressBar)
  toggleProgressBar(context: StateContext<EngineStateModel>, action: ToggleProgressBar) {
    context.patchState({
      showProgressBar: action.show
    });
  }

  @Action(ToggleCameraBar)
  toggleCameraBar(context: StateContext<EngineStateModel>, action: ToggleCameraBar) {
    context.patchState({
      showCameraBar: action.show
    });
  }

  @Action(ToggleLabels)
  toggleLabels(context: StateContext<EngineStateModel>, action: ToggleLabels) {
    const show = (action.show != null) ? action.show : !context.getState().showLabels;

    context.patchState({
      showLabels: show
    });
  }

  @Action(SynchronizeVisibleNodes)
  synchronizeVisibleNodes(context: StateContext<EngineStateModel>, action: SynchronizeVisibleNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafVisibilityState);

    // set unique view node leaf state by querying corresponding human object state
    const leafState = {};
    leafViewNodeIds.forEach(viewNodeId => {
      const leafids = this.engineService.getLeafObjects(viewNodeId);
      const visibility = leafids.reduce((vis, objectId) => vis || action.nodes[objectId].shown, false);
      leafState[viewNodeId] = { 'shown': visibility };
    });

    this.graphService.synchronize({...viewGraph}, { 'shown': 'trueWins' }, leafState);

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(SynchronizeRenderableNodes)
  synchronizeRenderableNodes(context: StateContext<EngineStateModel>, action: SynchronizeRenderableNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafRenderableState);

    // view graph leaf state
    const leafState = {};
    leafViewNodeIds.forEach(leafId => {
      leafState[leafId] = { 'renderable': true };
    });
    this.graphService.synchronize(viewGraph, { 'renderable': 'falseWins' }, leafState);
  }

  @Action(SynchronizePickableNodes)
  synchronizePickableNodes(context: StateContext<EngineStateModel>, action: SynchronizePickableNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafPickableState);

    // set unique view node leaf state by querying corresponding human object state
    const leafState = {};
    leafViewNodeIds.forEach(viewNodeId => {
      const leafids = this.engineService.getLeafObjects(viewNodeId);
      const pickable = leafids.reduce((pick, objectId) => pick || Human.scene.objects[objectId].pickable, false);

      leafState[viewNodeId] = { 'pickable': pickable };
    });
    this.graphService.synchronize(viewGraph, { 'pickable': 'trueWins' }, leafState);

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }


  // For animations
  // Need to resolve unpause action triggering the next timeline
  // This is because unpause is stupid in it's implementation
  // Also need to resolve firing play() initially on chapter to ensure we load in the appropriate settings for loop

  @Action(PlayChapterAnimation)
  playChapterAnimation(context: StateContext<EngineStateModel>, action: PlayChapterAnimation) {
    const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    const playCount = get(chapter, 'ui.play.count', 0);

    this.animationService.play({
      startChapterId: chapter.id,
      loop: (playCount === -1),
      numChapters: 1,
      queue: false // True for higher playcount
    });
    
    const playing = context.getState().animationPlaying;
    if (!playing) {
      this.animationService.pause();
    }
  }

  @Action(ToggleAnimation)
  toggleAnimation(context: StateContext<EngineStateModel>, action: ToggleAnimation) {
    const chapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);

    if (action.play) {
      this.animationService.unpause(chapterId);
    } else {
      this.animationService.pause();
    }
  }

  @Action(SetAnimationPlaying)
  setAnimationPlaying(context: StateContext<EngineStateModel>, action: SetAnimationPlaying) {
    context.patchState({
      animationPlaying: action.playing
    });
  }

  @Action(SetPlaybackSpeed)
  setPlaybackSpeed(context: StateContext<EngineStateModel>, action: SetPlaybackSpeed) {
    context.patchState({
      playbackSpeed: action.speed
    });
  }

  @Action(PlayAudio)
  playAudio(context: StateContext<EngineStateModel>, action: PlayAudio) {
    context.patchState({
      playAudio: action.play
    });
  }

  @Action(SetAutoplay)
  setAutoplay(context: StateContext<EngineStateModel>, action: SetAutoplay) {
    context.patchState({
      autoplay: action.enable
    });
  }

  @Action(SetObjectLayerOrder)
  setObjectLayerOrder(context: StateContext<EngineStateModel>, action: SetObjectLayerOrder) {

    const viewGraph = context.getState().viewGraph;
    const layer = isNaN(action.layer) ? 0 : action.layer;

    let nodeIds = [];

    action.nodes.forEach((node) => {
      const descendantIds = this.graphService.getDescendants(viewGraph, node.id, true);
      nodeIds = nodeIds.concat(descendantIds);
    });

    nodeIds.forEach((nodeId) => {
      this.engineService.setObjectLayer(nodeId, layer);
      this.graphService.setState(viewGraph, nodeId, { layer: layer });
    });

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(SetObjectDisplayName)
  setObjectDisplayName(context: StateContext<EngineStateModel>, action: SetObjectDisplayName) {
    const object = Human.scene.objects[action.nodeId];

    if (object) {
      object.displayName = action.displayName;
    }
  }

  @Action(AutoSortObjectLayers)
  autoSortObjectLayers(context: StateContext<EngineStateModel>, action: AutoSortObjectLayers) {
    const viewGraph = context.getState().viewGraph;
    const layerMultiple = 20;
    let updateLayer = 0;

    const leafIds = Human.scene.getEnabledLeaves();
    const objectLayerValues = this.engineService.getObjectLayerValues(leafIds);

    Object.keys(objectLayerValues)
      .map(layer => parseInt(layer, 10))
      .sort((a, b) => a - b) // Sort ascending
      .forEach((layer) => {

        objectLayerValues[layer]
          .sort((a, b) => {
            return b.value - a.value; // Sort descending
          })
          .forEach((objectLayerValue) => {
            const newLayer = updateLayer * layerMultiple;

            this.engineService.setObjectLayer(objectLayerValue.nodeId, newLayer);
            this.graphService.setState(viewGraph, objectLayerValue.nodeId, { layer: newLayer }); // Mutate the view graph nodes

            updateLayer++;
          });
    });

    // Trigger refresh of viewgraph
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(ResetObjectLayers)
  resetObjectLayers(context: StateContext<EngineStateModel>, action: ResetObjectLayers) {

    const viewGraph = context.getState().viewGraph;
    const leafIds = Human.scene.getEnabledLeaves();

    leafIds.forEach((leafId) => {
      this.engineService.setObjectLayer(leafId, 0);
      this.graphService.setState(viewGraph, leafId, { layer: 0 });
    });

    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(SetPostProcessingEffects)
  setPostProcessingEffects(context: StateContext<EngineStateModel>, { effects }: SetPostProcessingEffects) {
    this.engineService.setPostProcessingFeatures(effects);
  }

  @Action(SetProperties)
  setProperties(context: StateContext<EngineStateModel>, { properties }: SetProperties) {
    this.engineService.setProperties({
      ...properties
    });
  }

  @Action(SetTransparencyMode)
  setTransparencyMode(context: StateContext<EngineStateModel>, { mode }: SetTransparencyMode) {
    this.engineService.setTransparencyMode(mode);
  }


  @Action(ExpandViewTreeNode)
  expandNode(
    context: StateContext<EngineStateModel>,
    action: ExpandViewTreeNode
  ) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const ancestors = GraphUtils.getAncestors(
      viewGraph,
      action.node.id,
      true
    );
    ancestors.forEach(id => {
      const node = viewGraph.nodes[id];
      node.boolState.expanded = true;
    });

    this.store.dispatch(new SetGraph(viewGraph));
  }

  @Action(CollapseViewTreeNode)
  collapseNode(
    context: StateContext<EngineStateModel>,
    action: CollapseViewTreeNode
  ) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    viewGraph.nodes[action.node.id].boolState.expanded = false;
    this.store.dispatch(new SetGraph(viewGraph));
  }

  @Action(HoverNode)
  hoverNode(
    context: StateContext<EngineStateModel>,
    action: HoverNode
  ) {
    context.patchState({
      hoveredNode: action.node
    });
  }

  @Action(SetLabelEditNode)
  setLabelEditNode(
    context: StateContext<EngineStateModel>,
    action: SetLabelEditNode
  ) {
    context.patchState({
      labelEditNode: action.node
    });
  }

  @Action(QueryNodeInfo)
  queryNodeInfo(
    context: StateContext<EngineStateModel>,
    { node }: QueryNodeInfo
  ) {
    // begin querying
    context.patchState({ queryingWiki: true });

    // query wiki service
    this.wikiService
      .getWikiText(node.label)
      .pipe(
        // end querying
        finalize(() => context.patchState({ queryingWiki: false }))
      )
      .subscribe(
        // success
        wikiDescription =>
          context.dispatch(
            new QueryNodeInfoSuccess(node, wikiDescription)
          ),
        // failure
        () => context.dispatch(new QueryNodeInfoFailure(node))
      );
  }

  @Action(QueryNodeInfoSuccess)
  queryNodeInfoSuccess(
    context: StateContext<EngineStateModel>,
    { node, description }: QueryNodeInfoSuccess
  ) {
    context.dispatch(new SetNodeInfo(node, description));
  }

  @Action(SetNodeInfo)
  setNodeInfo(
    context: StateContext<EngineStateModel>,
    { node, description }: SetNodeInfo
  ) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const viewNode = viewGraph.nodes[node.id];
    if (viewNode) {
      viewNode.description = description;
      this.store.dispatch(new SetGraph(viewGraph));
    }
  }

  @Action(SetContextMenu)
  setContextMenu(
    context: StateContext<EngineStateModel>,
    action: SetContextMenu
  ) {
    context.patchState({
      contextmenu: { node: action.node, hit: action.hit }
    });
  }

  private stateMapToNodeIds(viewGraph: BdGraph<ViewTreeNode>, stateMap: BdGraphStateMap): Set<string> {
    // map human objects to relevant view nodes (if any);
    const ids = Object.keys(stateMap)
      .map(objectId => this.engineService.getNode(viewGraph, objectId))
      .filter(node => !!node)
      .map(node => node.id);

    return new Set<string>(ids);
  }

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private engineService: EngineService,
    private wikiService: WikiService,
    private animationService: AnimationService
  ) { }
}
